import Config from 'config';
import { fork, call, put, select } from 'redux-saga/effects';
import request from '../../../lib/request';
import waitForSession from '../../../lib/wait-for-session';
import { getUserId } from '../../session/getters';
import {
  paymentPlanId,
  installments
} from '../getters';
import { stripeProviderKey } from '../../stripe/getters';

import {
  ON_PAYMENT_PLAN_MOUNT_SUCCESS,
  ON_PAYMENT_PLAN_MOUNT_ERROR,
  PAY_INSTALLMENT_SUCCESS,
  PAY_INSTALLMENT_ERROR,
  PAY_INSTALLMENTS_SUCCESS,
  PAY_INSTALLMENTS_ERROR
} from '../actions';

import {
  ADD_FLASH_MESSAGE
} from '../../my-tickets/actions';

export function* requestAndPut(requestParameters, actionCreator) {
  const result = yield call(...requestParameters);
  yield put(actionCreator(result));
}

export const requestPaymentPlanActionCreator = paymentPlan => ({
  type: ON_PAYMENT_PLAN_MOUNT_SUCCESS,
  payload: paymentPlan ? paymentPlan : false
});

export function requestTicket(paymentPlanId) {
  const options = {
    credentials: 'include'
  };

  return request(`${Config.api.base}/tickets/${paymentPlanId}`, options)
    .then(resp => resp.json());
}

export function* fetchPaymentPlanData() {
  yield call(waitForSession);
  const userId = yield select(getUserId);

  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }

    const id = yield select(paymentPlanId);

    yield fork(requestAndPut, [requestTicket, id], requestPaymentPlanActionCreator);
  }
  catch (e) {
    yield put({ type: ON_PAYMENT_PLAN_MOUNT_ERROR, payload: e });
  }
}

async function handleAction(providerKey, paymentIntentClientSecret) {
  const stripe = Stripe(providerKey);
  const {
    error: errorAction,
    paymentIntent
  } = await stripe.handleCardAction(paymentIntentClientSecret);

  if (errorAction) {
    return { errorCode: errorAction.code, paymentIntent: null };
  }
  return { errorCode: null, paymentIntent };
}

function* pendingInstallmentPay(installmentData, number, res) {
  const { payment_intent_client_secret } = res;
  const providerKey = yield select(stripeProviderKey);
  const intentResponse = yield handleAction(providerKey, payment_intent_client_secret);
  if (intentResponse.errorCode){
    yield put({ type: PAY_INSTALLMENT_ERROR, payload: intentResponse });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: intentResponse.errorCode }});
  }
  else {
    const { paymentIntent } = intentResponse;
    const res = yield installmentIntentConfirmation(installmentData, number, paymentIntent);
    if (res && res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'payInstallmentError' }});
      yield put({ type: PAY_INSTALLMENT_ERROR, payload: res.errors });
    }
    else {
      yield put({ type: PAY_INSTALLMENT_SUCCESS, payload: res });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'payInstallmentSuccess' }});
    }
  }
}

function* pendingInstallmentsPay(ticketId, res) {
  const { payment_intent_client_secret } = res;
  const providerKey = yield select(stripeProviderKey);
  const intentResponse = yield handleAction(providerKey, payment_intent_client_secret);
  if (intentResponse.errorCode){
    yield put({ type: PAY_INSTALLMENTS_ERROR, payload: intentResponse });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: intentResponse.errorCode }});
  }
  else {
    const { paymentIntent } = intentResponse;
    const res = yield ticketIntentConfirmation(ticketId, paymentIntent);
    if (res && res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'payInstallmentError' }});
      yield put({ type: PAY_INSTALLMENTS_ERROR, payload: res.errors });
    }
    else {
      yield put({ type: PAY_INSTALLMENTS_SUCCESS, payload: res });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'payInstallmentSuccess' }});
    }
  }
}

function installmentIntentConfirmation(installments, number, paymentIntent) {
  const installment = installments
    .map(installment => { return { ...installment, token: null }; })
    .find(installment => installment.number === number);

  const url = `${Config.api.base}/installment_intents/${installment.id}/confirm`;
  const data = JSON.stringify({
    intent_id: paymentIntent.id
  });
  const options = {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: data,
  };
  return request(url, options).then(res => res.json());
}

function ticketIntentConfirmation(ticketId, paymentIntent) {
  const url = `${Config.api.base}/ticket_intents/${ticketId}/confirm`;
  const data = JSON.stringify({
    intent_id: paymentIntent.id,
    pay_installments: true
  });
  const options = {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: data,
  };
  return request(url, options).then(res => res.json());
}

export function* payInstallment({ payload }) {
  function pay(installments, number) {
    const installment = installments
      .map(installment => {
        installment.token = null;
        return installment;
      })
      .find(installment => installment.number === number);

    const options = {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ installment })
    };

    return request(`${Config.api.base}/installments/${installment.id}`, options)
      .then(res => res.json());
  }

  try {
    const installmentData = yield select(installments);
    const res = yield pay(installmentData, payload.number);

    if (res && res.errors) {
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'payInstallmentError' }});
      yield put({ type: PAY_INSTALLMENT_ERROR, payload: res.errors });
    }
    else if (res && res.state === 'pending') {
      yield pendingInstallmentPay(installmentData, payload.number, res);
    }
    else {
      yield put({ type: PAY_INSTALLMENT_SUCCESS, payload: res });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'payInstallmentSuccess' }});
    }
  }
  catch (e) {
    yield put({ type: PAY_INSTALLMENT_ERROR, payload: e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'payInstallmentError' }});
  }
}

export function* payAllInstallments() {
  function pay(ticketId) {
    const options = {
      method: 'PUT',
      credentials: 'include'
    };

    return request(`${Config.api.base}/tickets/${ticketId}/pay_installments`, options)
      .then(res => res.json());
  }

  try {
    const ticketId = yield select(paymentPlanId);
    const res = yield pay(ticketId);

    if (res && res.errors) {
      yield put({ type: PAY_INSTALLMENTS_ERROR, payload: e });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'payInstallmentError' }});
    }
    else if (res && res.state === 'pending') {
      yield pendingInstallmentsPay(ticketId, res);
    }
    else {
      yield put({ type: PAY_INSTALLMENTS_SUCCESS, payload: res });
      yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'success', message: 'payInstallmentSuccess' }});
    }
  }
  catch (e) {
    yield put({ type: PAY_INSTALLMENTS_ERROR, payload: e });
    yield put({ type: ADD_FLASH_MESSAGE, payload: { level: 'danger', message: 'payInstallmentError' }});
  }
}



// WEBPACK FOOTER //
// ./src/modules/payment-plans/actions/sagas.js